import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "../../../lib/utils"
import { buttonVariants } from "./button.variants"

export interface BaseButtonProps extends Omit<React.ComponentProps<"button">, "size"> {
  /**
   * If true, the button will render as its child element (useful for links styled as buttons)
   */
  asChild?: boolean
  /**
   * If true, shows a loading spinner and disables the button
   */
  loading?: boolean
  /**
   * Icon to display before the button text
   */
  leadingIcon?: React.ReactNode
  /**
   * Icon to display after the button text
   */
  trailingIcon?: React.ReactNode
  /**
   * If true, the button will take up the full width of its container
   */
  fullWidth?: boolean
  /**
   * Button variant style
   */
  variant?: VariantProps<typeof buttonVariants>["variant"]
}

export type ButtonProps = BaseButtonProps & (
  | {
      /**
       * Button size
       */
      size: "icon" | "icon-xs" | "icon-sm" | "icon-lg"
      /**
       * Accessible name is REQUIRED for icon-only buttons
       */
      "aria-label": string
    }
  | {
      /**
       * Button size
       */
      size?: "default" | "xs" | "sm" | "md" | "lg" | null
      "aria-label"?: string
    }
)

export function Button({
  className,
  variant = "default",
  size = "md",
  asChild = false,
  loading = false,
  leadingIcon,
  trailingIcon,
  fullWidth = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button"
  const isDisabled = disabled || loading

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-busy={loading}
      className={cn(
        buttonVariants({ variant, size, className }),
        fullWidth && "w-full"
      )}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          {children}
        </>
      ) : (
        <>
          {leadingIcon}
          {children}
          {trailingIcon}
        </>
      )}
    </Comp>
  )
}

Button.displayName = 'Button'

export default Button
